import { useState, useEffect } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import Card from '../../components/ui/Card';
import { callFn } from '../contracts/contractsApi';

const ORANGE = '#E8610A';
const RED = '#DC2626';
const GREEN = '#3B6D11';
const GREY = '#6B7280';

const label = (period, mode) => {
  const d = new Date(`${period}T12:00:00Z`);
  const s = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return mode === 'weekly' ? `w/c ${s}` : s;
};

// Fleet activity for one Ops organisation: check completion (share of active
// machines checked in the period) and faults reported vs resolved.
export default function ActivityCharts({ orgId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('daily');

  useEffect(() => {
    callFn('getOpsActivity', { orgId }).then(setData).catch(e => setError(e.message));
  }, [orgId]);

  if (error) return <Card><div style={{ fontSize: 12, color: 'var(--color-danger)' }}>{error}</div></Card>;
  if (!data) return <Card><div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Loading activity...</div></Card>;

  const rows = data[mode].map(r => ({ ...r, label: label(r.period, mode) }));
  const s = data.summary;

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14 }}>Activity</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
            Completion is the share of active machines with at least one check in the {mode === 'daily' ? 'day' : 'week'}.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['daily', 'Daily, 30 days'], ['weekly', 'Weekly, 12 weeks']].map(([m, l]) => (
            <div key={m} onClick={() => setMode(m)} style={{
              padding: '5px 12px', borderRadius: 'var(--radius-full)', fontSize: 12, cursor: 'pointer',
              background: mode === m ? 'var(--color-brand)' : 'var(--color-bg-primary)',
              color: mode === m ? '#fff' : 'var(--color-text-secondary)',
              border: mode === m ? 'none' : '0.5px solid var(--color-border-light)',
            }}>{l}</div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
        <Stat label="Active machines" value={s.fleet} />
        <Stat label={mode === 'daily' ? 'Avg daily completion' : 'Avg weekly completion'} value={pctText(mode === 'daily' ? s.avgDailyCompletion : s.avgWeeklyCompletion)} />
        <Stat label="Open faults" value={s.openFaults} tone={s.openFaults ? 'var(--color-warning)' : null} />
        <Stat label="Open critical faults" value={s.openCriticalFaults} tone={s.openCriticalFaults ? 'var(--color-danger)' : null} />
      </div>

      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Check completion</div>
      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="var(--color-border-light)" strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: GREY }} interval="preserveStartEnd" minTickGap={16} />
            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10, fill: GREY }} />
            <Tooltip formatter={(v, n, p) => n === 'Completion' ? [`${v ?? 0}% (${p.payload.checked} of ${p.payload.fleet} machines)`, n] : [v, n]} />
            <Line type="monotone" dataKey="completion" name="Completion" stroke={ORANGE} strokeWidth={2} dot={mode === 'weekly'} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '18px 0 6px' }}>Faults</div>
      <div style={{ width: '100%', height: 200 }}>
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="var(--color-border-light)" strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: GREY }} interval="preserveStartEnd" minTickGap={16} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: GREY }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="faultsReported" name="Reported" stroke={RED} strokeWidth={2} dot={mode === 'weekly'} />
            <Line type="monotone" dataKey="faultsResolved" name="Resolved" stroke={GREEN} strokeWidth={2} dot={mode === 'weekly'} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

const pctText = (v) => (v == null ? '—' : `${v}%`);

function Stat({ label, value, tone }) {
  return (
    <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 20, marginTop: 4, color: tone || 'var(--color-text-primary)' }}>{value ?? '—'}</div>
    </div>
  );
}
